import styleguide from 'aria-viewer/tour/constants/styleGuide'
import React, { PureComponent, Component } from 'react'
import pure from 'recompose/pure'
import $ from 'jquery'

const Dots = pure(function Dots({
    count,
    index,
    size = 0.3,
    bottom = '11px',
    margin = '0.25em',
    border = '0.05em solid white',
    activeColor = 'white',
    inactiveColor = 'transparent',
}) {
    return (
        <div
            style={{
                position: 'absolute',
                left: '50%',
                transform: 'translate3d(-50%, 0, 1px)',
                bottom,
                zIndex: 1000,
                whiteSpace: 'nowrap',
            }}
        >
            {Array.from({ length: count }, () => 0).map((item, i) => (
                <div
                    key={`carouselItem${index}DotIndex${i}`}
                    style={{
                        width: size + 'em',
                        height: size + 'em',
                        border,
                        display: 'inline-block',
                        borderRadius: '50%',
                        background: index === i ? activeColor : inactiveColor,
                        transition: 'all .3s',
                        margin,
                    }}
                />
            ))}
        </div>
    )
})

const CarouselItem = pure(function CarouselItem({ onClick, style, children, index }) {
    return (
        <div onClick={e => onClick && onClick(e, index)} style={style}>
            {children}
        </div>
    )
})

class Carousel extends Component {
    constructor(props) {
        super(props)

        //The currently selected item
        const selectedItemIndex = props.index || 0
        //Allow the props to show 1 to n items at a time
        const maxItemsToDisplay = Math.max(1, props.maxItemsToDisplay || 7)
        //Used to determine the first item we'll be displaying
        const minItemIndex = this.getMinItemIndex({
            index: selectedItemIndex,
            max: maxItemsToDisplay,
        })

        this.state = {
            selectedItemIndex,
            maxItemsToDisplay,
            minItemIndex,
        }

        this.$ = { carousel: undefined }

        const offset = props.outerWidth / 2 - props.innerWidth / 2
        this.BaseStyle = {
            width: props.innerWidth,
            height: props.height,
            display: 'inline-block',
            whiteSpace: 'initial',
            verticalAlign: 'top',
            transition: `width ${styleguide.delay.normal}`,
            position: 'relative',
        }
        this.LeftStyle = Object.assign({}, this.BaseStyle, {
            marginLeft: offset,
        })
        this.RightStyle = Object.assign({}, this.BaseStyle, {
            marginRight: offset,
        })
    }
    getJqCarouselRef = () => {
        if (!this.$.carousel) {
            this.$.carousel = $(this.refs.carousel)
        }
        return this.$.carousel
    }
    scrollTo({ index, instant, callback }) {
        this.getJqCarouselRef().animate(
            {
                scrollLeft: index * this.props.innerWidth,
            },
            instant ? 0 : parseInt(styleguide.delay.fast),
            callback
        )
    }
    goTo = ({ e, index = 0, internal = false, instant = false }) => {
        const { onChange, innerWidth } = this.props

        const minItemIndex = this.getMinItemIndex()

        if (this.state.selectedItemIndex != index) {
            this.setState({ selectedItemIndex: index })
        }
        this.scrollTo({
            index,
            instant,
            callback: () => {
                if (internal && onChange) {
                    onChange(null, index, internal)
                }
            },
        })
    }

    componentWillUnmount() {
        this.getJqCarouselRef().stop(true, true)
    }
    componentDidMount() {
        //Make sure we're looking at the right card
        const { index } = this.props
        if (this.getCurrentItemIndex() != index) {
            this.goTo({ index, instant: true })
        }
    }
    componentWillReceiveProps(nextProps) {
        if (typeof nextProps.index != 'undefined') {
            //If we specified a new index, make sure we navigate to it
            if (nextProps.index != this.state.selectedItemIndex) {
                this.goTo({
                    index: nextProps.index,
                    instant: true,
                })
            } else if (this.refs.carousel) {
                //If we're not focued on the currently selected item
                //scroll it into view
                if (this.state.selectedItemIndex != this.getCurrentItemIndex()) {
                    this.goTo({
                        index: this.state.selectedItemIndex,
                        instant: true,
                    })
                }
            }
        }
    }
    //Get the index of the card that's closest to
    //the position of where the selected card would be if it was focused
    getCurrentItemIndex = () => {
        const { innerWidth } = this.props
        const scrollLeft = this.getJqCarouselRef().scrollLeft()
        return Math.round(scrollLeft / innerWidth)
    }
    //Get the index of the first item to display
    getMinItemIndex = (params = {}) => {
        //Allow index and max to be passed to this function
        const index = typeof params.index != 'undefined' ? params.index : this.getCurrentItemIndex()
        const maxItemsToDisplay = params.max || this.state.maxItemsToDisplay
        const maxPreviousItems = Math.round(index - maxItemsToDisplay / 2)

        return Math.max(0, maxPreviousItems)
    }

    start = e => {
        if (this.props.discrete && !this.props.disabled) {
            this.getJqCarouselRef().stop()
            this.down = e.touches[0].pageX
        }
    }

    move = e => {
        if (this.props.discrete && !this.props.disabled) {
            e.preventDefault()
            this.last = e.touches[0].pageX
        }
    }

    end = e => {
        if (this.props.discrete && !this.props.disabled) {
            const SENSITIVITY = 50
            const { selectedItemIndex } = this.state
            if (this.down - this.last >= SENSITIVITY) {
                this.goTo({
                    e,
                    index: Math.min(selectedItemIndex + 1, this.props.items.length - 1),
                    internal: true,
                })
            } else if (this.down - this.last <= -SENSITIVITY) {
                this.goTo({
                    e,
                    index: Math.max(selectedItemIndex - 1, 0),
                    internal: true,
                })
            }
        }
    }

    onScroll = e => {
        e.stopPropagation()

        const { minItemIndex } = this.state
        const newMinIndex = this.getMinItemIndex()
        //See if we need to recalculate what cards to show
        if (minItemIndex != newMinIndex) {
            this.setState({ minItemIndex: newMinIndex })
        }
    }

    handleOnClick = (e, index) => {
        this.goTo({ e, index, internal: true })
        this.props.onClick && this.props.onClick(index)
    }

    render() {
        const { outerWidth, height, style, discrete, disabled, items, dots } = this.props
        const { selectedItemIndex, minItemIndex, maxItemsToDisplay } = this.state

        return (
            <div style={style}>
                <div
                    onScroll={this.onScroll}
                    onTouchStart={this.start}
                    onTouchMove={this.move}
                    onTouchEnd={this.end}
                    onTouchCancel={this.end}
                    ref="carousel"
                    style={{
                        width: outerWidth,
                        height: height,
                        overflow: disabled || discrete ? 'hidden' : 'scroll',
                        whiteSpace: 'nowrap',
                        WebkitOverflowScrolling: 'touch',
                        WebkitBackfaceVisibility: 'hidden',
                    }}
                >
                    {items &&
                        items.map(
                            (child, index) =>
                                child.Tag && (
                                    <CarouselTagItem
                                        key={`carouselTagItem_${index}`}
                                        onClick={this.handleOnClick}
                                        index={index}
                                        style={
                                            index === 0
                                                ? this.LeftStyle
                                                : index === items.length - 1
                                                    ? this.RightStyle
                                                    : this.BaseStyle
                                        }
                                        Tag={child.Tag}
                                        props={child.props}
                                        isSelected={index == selectedItemIndex}
                                        visible={index >= minItemIndex && index < minItemIndex + maxItemsToDisplay}
                                    />
                                )
                        )}
                </div>
                {dots &&
                    items.length > 1 && (
                        <Dots index={selectedItemIndex} count={items.length} {...this.props.dotStyle} />
                    )}
            </div>
        )
    }
}

const CarouselTagItem = pure(function CarouselTagItem({ onClick, style, index, isSelected, Tag, props, visible }) {
    return (
        <CarouselItem onClick={onClick} index={index} style={style}>
            {
                <Tag
                    {...props}
                    style={Object.assign(
                        {},
                        props.style,
                        // When visible change display
                        // to prevent manipulating the dom
                        visible
                            ? {
                                  display: 'inline-block',
                              }
                            : {
                                  display: 'none',
                              }
                    )}
                    index={index}
                    isSelected={isSelected}
                />
            }
        </CarouselItem>
    )
})
CarouselTagItem.displayName = 'CarouselTagItem'

export default Carousel
